import { Backoff, sleep } from "../net/backoff.js";
import { loadHlsBundle } from "./hls.js";
import { WhepError, connect, type WhepSession } from "./whep.js";

/** Что показывать в бейдже над картинкой. */
export interface Health {
  transport: "webrtc" | "hls" | "none";
  fps: number;
  width: number;
  height: number;
  rttMs: number | null;
  /** Какой тип кандидата победил: host / srflx / relay. */
  candidate: string | null;
  /** udp или tcp. Переход на tcp — ранний признак, что скоро понадобится HLS. */
  protocol: string | null;
  packetsLost: number;
  state: string;
}

/**
 * Сколько секунд без новых кадров считать зависанием.
 *
 * Шесть, а не два: короткая заминка на границе GOP или моргание сети не должны
 * рвать рабочую сессию. Но и ждать дольше нельзя — на антикражной системе
 * замерший кадр, выглядящий живым, хуже честного «переподключаюсь».
 */
const STALL_LIMIT_SECS = 6;

/**
 * Проигрыватель одной камеры.
 *
 * Главное, чего нет в готовых клиентах: **сторожевой таймер простоя**. WebRTC
 * совершенно спокойно остаётся в `connectionState === "connected"` после того,
 * как кадры перестали приходить: энкодер на той стороне заклинило, SRT-канал
 * умер без RTCP-прощания, публикатора нет вовсе. Без этой проверки оператор
 * смотрит на замерший кадр и считает его живым — на системе видеонаблюдения
 * это и есть худший из отказов.
 */
export class Player {
  private session: WhepSession | null = null;
  private readonly backoff = new Backoff();
  private stopped = false;
  private stalledFor = 0;
  private lastFrames = 0;
  private poll: number | null = null;
  private health: Health = {
    transport: "none",
    fps: 0,
    width: 0,
    height: 0,
    rttMs: null,
    candidate: null,
    protocol: null,
    packetsLost: 0,
    state: "подключение",
  };

  constructor(
    readonly camera: string,
    private readonly video: HTMLVideoElement,
    private readonly onHealth: (health: Health) => void,
  ) {}

  start(): void {
    this.stopped = false;
    void this.loop();
  }

  async stop(): Promise<void> {
    this.stopped = true;
    if (this.poll !== null) window.clearInterval(this.poll);
    await this.teardown();
  }

  private async teardown(): Promise<void> {
    if (this.poll !== null) {
      window.clearInterval(this.poll);
      this.poll = null;
    }
    const session = this.session;
    this.session = null;
    if (session) await session.close();
  }

  private report(patch: Partial<Health>): void {
    this.health = { ...this.health, ...patch };
    this.onHealth(this.health);
  }

  private async loop(): Promise<void> {
    while (!this.stopped) {
      try {
        await this.attempt();
        // Досюда доходим только после разрыва — сразу пробуем снова.
      } catch (err) {
        if (this.stopped) return;

        if (err instanceof WhepError && err.status === 401) {
          this.report({ transport: "none", state: "нужно войти" });
          return;
        }
        if (err instanceof WhepError && err.status === 403) {
          this.report({ transport: "none", state: "доступа нет" });
          return;
        }
        if (err instanceof WhepError && err.status === 503) {
          this.report({ transport: "none", state: "слишком много зрителей" });
        } else if (err instanceof WhepError && err.status === 404) {
          this.report({ transport: "none", state: "никто не публикует" });
        } else {
          this.report({ transport: "none", state: "переподключение" });
        }

        // Два провала подряд по любой причине, кроме отказов доступа, — повод
        // уйти на запасной путь: у зрителя может быть просто заблокирован UDP.
        if (this.backoff.attempts >= 2) {
          if (await this.tryHls()) return;
        }
      }
      await this.teardown();
      if (this.stopped) return;
      await sleep(this.backoff.next());
    }
  }

  /** Одна попытка: держит соединение до разрыва или до зависания. */
  private async attempt(): Promise<void> {
    this.report({ state: "подключение", transport: "none" });
    const session = await connect(this.camera, (stream) => {
      this.video.srcObject = stream;
      void this.video.play().catch(() => {
        // Автовоспроизведение могло быть запрещено — это не наша ошибка, и
        // молчаливый отказ здесь правильнее падения.
      });
    });
    this.session = session;
    this.stalledFor = 0;
    this.lastFrames = 0;

    const broken = new Promise<string>((resolve) => {
      session.pc.addEventListener("connectionstatechange", () => {
        const state = session.pc.connectionState;
        if (state === "failed" || state === "disconnected" || state === "closed") {
          resolve(`соединение ${state}`);
        }
      });
      this.poll = window.setInterval(() => {
        void this.sample(session, resolve);
      }, 2000);
    });

    const reason = await broken;
    throw new WhepError(reason, null);
  }

  /** Снять статистику и заодно проверить, идут ли кадры. */
  private async sample(
    session: WhepSession,
    fail: (reason: string) => void,
  ): Promise<void> {
    let frames = 0;
    let patch: Partial<Health> = {};

    const stats = await session.pc.getStats();
    stats.forEach((report) => {
      if (report.type === "inbound-rtp" && report.kind === "video") {
        frames = report.framesDecoded ?? 0;
        patch = {
          ...patch,
          transport: "webrtc",
          fps: Math.round(report.framesPerSecond ?? 0),
          width: report.frameWidth ?? 0,
          height: report.frameHeight ?? 0,
          packetsLost: report.packetsLost ?? 0,
        };
      }
      if (report.type === "candidate-pair" && report.state === "succeeded") {
        patch = {
          ...patch,
          rttMs:
            report.currentRoundTripTime === undefined
              ? null
              : Math.round(report.currentRoundTripTime * 1000),
        };
      }
      if (report.type === "local-candidate" && report.candidateType) {
        patch = {
          ...patch,
          candidate: report.candidateType,
          protocol: report.protocol ?? null,
        };
      }
    });

    if (frames > 0 && frames === this.lastFrames) {
      this.stalledFor += 2;
      if (this.stalledFor >= STALL_LIMIT_SECS) {
        fail(`кадры не идут ${this.stalledFor} с`);
        return;
      }
      this.report({ ...patch, state: "кадры не идут" });
      return;
    }

    if (frames > this.lastFrames) {
      this.stalledFor = 0;
      // Сброс отката ТОЛЬКО здесь: по факту идущих кадров, а не по факту
      // установленного соединения.
      this.backoff.reset();
      this.report({ ...patch, state: "живой" });
    }
    this.lastFrames = frames;
  }

  /**
   * Запасной путь: LL-HLS через то же приложение.
   *
   * Нужен зрителю, у которого исходящий UDP и 8189/tcp закрыты, — такое бывает
   * в корпоративных сетях, где наружу пускают только 80 и 443. Задержка около
   * секунды-двух против суб-секундной у WebRTC, и бейдж обязан об этом честно
   * сказать: принять запись с задержкой за живую картинку на антикражной
   * системе — настоящая беда.
   */
  private async tryHls(): Promise<boolean> {
    const url = `/api/hls/${encodeURIComponent(this.camera)}/index.m3u8`;
    if (this.video.canPlayType("application/vnd.apple.mpegurl") !== "") {
      this.video.srcObject = null;
      this.video.src = url;
      void this.video.play().catch(() => {});
      this.report({ transport: "hls", state: "с задержкой" });
      return true;
    }

    try {
      const Hls = await loadHlsBundle();
      if (!Hls.isSupported()) return false;
      this.video.srcObject = null;
      const hls = new Hls({ lowLatencyMode: true, backBufferLength: 30 });
      hls.loadSource(url);
      hls.attachMedia(this.video);
      this.report({ transport: "hls", state: "с задержкой" });
      return true;
    } catch (err) {
      console.warn("запасной HLS недоступен", err);
      return false;
    }
  }
}
